import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import { AutoEvaluation } from '../../../../../interfaces/autoevaluation/autoevaluation.entity';
import { Comment } from '../../../../../interfaces/core/comment.entity';
import { CommentEditorComment } from '../../comment-editor/comment-editor.component';
import { AutoevaluationResponse, AnswerToSend, ScoreToSend, ManagementProfileResponse } from './types/autoevaluacion.types';
import { ScoreRules } from '../../../../../interfaces/autoevaluation/score-rules.entity';
import { ComponentScore } from '../../../../../interfaces/autoevaluation/component-score.entity';
import { Institution } from '../../../../../interfaces/core/institution.entity';
import { getJsDate } from '../../../../../utils/date';
import { clientHeaders } from '../../../../../utils/services';
import { MetaData } from '../../../../../interfaces/core/meta-data.entity';

type BaseEvaluationResponse = {
	id: string;
	start_date: string;
	end_date: string;
	year: number;
	version: number;
}

@Injectable({
	providedIn: 'root'
})
export class AutoevaluacionService {
	private baseUrl: string = environment.baseUrl;

	constructor(private http: HttpClient) { }

	getAutoevaluationsByInstitution(institutionId: string): Observable<AutoEvaluation[]> {
		return this.http.get<AutoevaluationResponse[]>(`${this.baseUrl}/autoevaluation/institution/${institutionId}`).pipe(
			map((response: AutoevaluationResponse[]) => {
				return response.map(autoEvaluationResponse => {
					return this.mapAutoEvaluation(autoEvaluationResponse);
				});
			})
		);
	}

	getById(autoEvaluationId: string) {
		return this.http.get<AutoevaluationResponse>(`${this.baseUrl}/autoevaluation/${autoEvaluationId}`).pipe(
			map(autoEvaluationResponse => this.mapAutoEvaluation(autoEvaluationResponse))
		);
	}

	getAutoevaluationComments(autoevaluationId: string) {
		return this.http.get<Comment[]>(`${this.baseUrl}/autoevaluation/comments/${autoevaluationId}`).pipe(map(
			response => response.map((comment) => ({ ...comment, created_at: getJsDate(comment.created_at as string, 'ISO') }))
		));
	}

	getBaseEvaluation() {
		return this.http.get<BaseEvaluationResponse | null>(`${this.baseUrl}/autoevaluation/baseevaluation`).pipe(
			map(response => {
				if (!response) {
					return null;
				}
				return {
					...response,
					start_date: getJsDate(response.start_date, 'ISO'),
					end_date: getJsDate(response.end_date, 'ISO'),
				};
			})
		);
	}

	createBaseAutoevaluation(
		institutionId: string ='',
		baseEvaluationId: string =''
	): Observable<{ id: string }> {
		return this.http.post<{ id: string }>(`${this.baseUrl}/autoevaluation/baseautoevaluation/`, {
			id_institution: institutionId,
			id_base_evaluation: baseEvaluationId
		});
	}

	createAutoevaluation(
		institutionId: string,
		baseEvaluationId: string,
		answers: AnswerToSend[],
		scores: ScoreToSend[]
	): Observable<{ id: string }> {
		return this.http.post<{ id: string }>(`${this.baseUrl}/autoevaluation`, {
			id_institution: institutionId,
			id_base_evaluation: baseEvaluationId,
			scores,
			answers
		});
	}

	updateAutoevaluation(
		autoEvaluationId: string,
		status: string,
		answers: AnswerToSend[],
		scores: ScoreToSend[],
		rejected: boolean = false
	): Observable<string> {
		return this.http.put(`${this.baseUrl}/autoevaluation/${autoEvaluationId}`, {
			status,
			scores,
			answers,
			rejected
		}, { responseType: 'text' });
	}

	getComponentComments(componentId: string) {
		return this.http.get<Comment[]>(`${this.baseUrl}/autoevaluation/comments/component/${componentId}`);
	}

	saveComment(autoevaluationId: string, comment: CommentEditorComment): Observable<string> {
		return this.http.post(`${this.baseUrl}/autoevaluation/comments/component/${comment.id_commentable}`, {
			id_autoevaluation: autoevaluationId,
			comment: comment.comment,
		}, { responseType: 'text' });
	}

	getManagementsProfile(autoevaluationId: string): Observable<ManagementProfileResponse[]> {
		return this.http.get<ManagementProfileResponse[]>(`${this.baseUrl}/route/managements-profile/${autoevaluationId}`);
	}

	getScoreRules(): Observable<ScoreRules[]> {
		return this.http.get<ScoreRules[]>(`${this.baseUrl}/autoevaluation/score-rules`);
	}

	getComponentScore(): Observable<ComponentScore[]> {
		return this.http.get<ComponentScore[]>(`${this.baseUrl}/autoevaluation/component-score`);
	}

	loadExcel(autoevaluationId: string, file: File): Observable<string> {
		const formData = new FormData();
		formData.append('file', file);

		return this.http.post(`${this.baseUrl}/autoevaluation/load-file/${autoevaluationId}`, formData, {
			responseType: 'text',
			headers: clientHeaders,
			withCredentials: true
		});
	}

	getPruebasSaber(institution?: Institution) {
		let pruebasSaber: MetaData | null = null;
		if (institution) {
			const temp = (institution.pruebasSaber ?? [])
				.filter(meta => meta.type === 'prueba_saber')
				.sort(function (a, b) {
					return (+b.key - +a.key);
				});

			const metaData = temp.shift();
			if (metaData) {
				pruebasSaber = metaData;
			}
		}

		return pruebasSaber;
	}

	private mapAutoEvaluation(autoEvaluationResponse: AutoevaluationResponse): AutoEvaluation {
		return {
			...autoEvaluationResponse,
			baseEvaluation: autoEvaluationResponse.baseEvaluation ? {
				...autoEvaluationResponse.baseEvaluation,
				start_date: getJsDate(autoEvaluationResponse.baseEvaluation.start_date, 'ISO'),
				end_date: getJsDate(autoEvaluationResponse.baseEvaluation.end_date, 'ISO'),
			} : undefined,
			withEvidences: autoEvaluationResponse.withEvidences,
			withoutEvidences: autoEvaluationResponse.withoutEvidences,
		};
	}
}
